import React, { useState, useEffect } from 'react';
import { MapPin, Phone, Mail, Star } from 'lucide-react';
import { pictureApi, testimonialApi, contactApi } from '../api';
import Logo from '../components/Logo';

const HomePage = () => {
  const [pictures, setPictures] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [contactStatus, setContactStatus] = useState('');
  const currentYear = new Date().getFullYear();
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);
  const prisonGallery = [
    `${process.env.PUBLIC_URL}/prison-1.png`,
    `${process.env.PUBLIC_URL}/prison-2.png`,
    `${process.env.PUBLIC_URL}/prison-3.png`,
    `${process.env.PUBLIC_URL}/prison-4.png`,
    `${process.env.PUBLIC_URL}/prison-5.png`,
    `${process.env.PUBLIC_URL}/prison-6.png`,
    `${process.env.PUBLIC_URL}/prison-7.png`,
  ];
  const sunsetGallery = [
    `${process.env.PUBLIC_URL}/sunset-1.png`,
    `${process.env.PUBLIC_URL}/sunset-2.png`,
  ];
  const spiceGallery = [
    `${process.env.PUBLIC_URL}/spice-5258020266449649078.png`,
    `${process.env.PUBLIC_URL}/spice-10310643970324882427.png`,
    `${process.env.PUBLIC_URL}/spice-8644762430218273544.png`,
    `${process.env.PUBLIC_URL}/spice-7707391809891704006.png`,
    `${process.env.PUBLIC_URL}/spice-7886741026394292428.png`,
    `${process.env.PUBLIC_URL}/spice-10912904380013245803.png`,
    `${process.env.PUBLIC_URL}/spice-12866551082645039428.png`,
    `${process.env.PUBLIC_URL}/spice-1042889488187328813.png`,
    `${process.env.PUBLIC_URL}/spice-8398822640325655027.png`,
    `${process.env.PUBLIC_URL}/spice-7580598496225883137.png`,
    `${process.env.PUBLIC_URL}/spice-13102894878969301111.png`,
    `${process.env.PUBLIC_URL}/spice-6973329492725276646.png`,
    `${process.env.PUBLIC_URL}/spice-10759744974732673136.png`,
    `${process.env.PUBLIC_URL}/spice-2499227156389724203.png`,
    `${process.env.PUBLIC_URL}/spice-7202975838062369307.png`,
    `${process.env.PUBLIC_URL}/spice-12575792703838740716.png`,
    `${process.env.PUBLIC_URL}/spice-12011902321655414236.png`,
  ];
  const [activeGallery, setActiveGallery] = useState(prisonGallery);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [picturesRes, testimonialsRes] = await Promise.all([
        pictureApi.getAll(),
        testimonialApi.getAll()
      ]);
      setPictures(picturesRes.data);
      setTestimonials(testimonialsRes.data);
    } catch (error) {
      console.log('Using default data - backend may not be running');
    }
  };

  const handleContactChange = (e) => {
    setContactForm({
      ...contactForm,
      [e.target.name]: e.target.value
    });
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setContactStatus('Sending...');
    
    try {
      await contactApi.submit(contactForm);
      setContactStatus('Message sent successfully!');
      setContactForm({ name: '', email: '', message: '' });
      setTimeout(() => setContactStatus(''), 5000);
    } catch (error) {
      setContactStatus('Failed to send message. Please try again.');
      setTimeout(() => setContactStatus(''), 5000);
    }
  };

  const defaultPictures = [
    { id: 1, title: 'Stone Town', imageUrl: 'https://images.unsplash.com/photo-1586861635167-e5223aadc9fe?w=600', description: 'Historic UNESCO World Heritage Site' },
    { id: 2, title: 'Nungwi Beach', imageUrl: 'https://images.unsplash.com/photo-1590523741831-ab7e8b8f9c7f?w=600', description: 'Crystal clear waters and white sand' },
    { id: 3, title: 'Spice Farm', imageUrl: 'https://images.unsplash.com/photo-1596040033229-a9821ebd058d?w=600', description: 'Explore the Spice Island' },
  ];

  const defaultTestimonials = [
    { id: 1, clientName: 'Sarah Johnson', clientCountry: 'USA', message: 'An absolutely magical experience! The tour guide was knowledgeable and the beaches were breathtaking.', rating: 5, clientPhoto: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100' },
    { id: 2, clientName: 'Marco Rossi', clientCountry: 'Italy', message: 'Best vacation ever! The spice tour was incredible and Stone Town is a must-see.', rating: 5, clientPhoto: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100' },
    { id: 3, clientName: 'Emma Wilson', clientCountry: 'UK', message: 'Professional service from start to finish. Highly recommend for anyone visiting Zanzibar!', rating: 5, clientPhoto: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100' },
  ];

  const displayPictures = pictures.length > 0 ? pictures : defaultPictures;
  const displayTestimonials = testimonials.length > 0 ? testimonials : defaultTestimonials;

  const renderStars = (rating) => {
    return [...Array(rating || 5)].map((_, i) => (
      <Star key={i} size={18} fill="#fbbf24" color="#fbbf24" />
    ));
  };

  return (
    <div>
      {/* Navigation */}
      <nav className="navbar">
        <div className="container">
        <div className="logo">
          <Logo className="logo-image" height={40} />
        </div>
          <ul className="nav-links">
            <li><a href="#home">Home</a></li>
            <li><a href="#about">About</a></li>
            <li><a href="#tours">Tours</a></li>
            <li><a href="#gallery">Gallery</a></li>
            <li><a href="#testimonials">Testimonials</a></li>
            <li><a href="#contact">Contact</a></li>
          </ul>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="hero" style={{
        backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url(${process.env.PUBLIC_URL}/hero-paradise.png)`
      }}>
        <div className="hero-content" style={{display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center'}}>
          <h1>Discover Paradise in Zanzibar</h1>
          <p style={{textAlign: 'center', maxWidth: '600px'}}>Experience the magic of pristine beaches, rich culture, and unforgettable adventures on the Spice Island</p>
          <a href="#tours" className="btn btn-primary">Explore Tours</a>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="about">
        <div className="container">
          <div className="about-content">
            <div className="about-image">
              <img src="https://images.unsplash.com/photo-1519659528534-7fd733a832a0?w=600" alt="Zanzibar" />
            </div>
            <div className="about-text">
              <h3 className="about-heading" aria-label="SaiTour">
                Welcome to SaiTour
              </h3>
              <p>We are your trusted local guides to the beautiful island of Zanzibar. With years of experience, we offer personalized tours that showcase the best of what this paradise has to offer.</p>
              <p>From the historic streets of Stone Town to the pristine beaches of Nungwi, from aromatic spice farms to underwater adventures, we create memories that last a lifetime.</p>
              <a href="#contact" className="btn btn-secondary">Get in Touch</a>
            </div>
          </div>
        </div>
      </section>

      {/* Tours Section */}
      <section id="tours" className="tours">
        <div className="container">
          <div className="section-title">
            <h2>Our Popular Tours</h2>
            <p>Choose from our carefully curated experiences</p>
          </div>
          <div className="tours-grid">
            <div className="tour-card" onClick={() => { setActiveGallery(prisonGallery); setIsGalleryOpen(true);} } role="button" tabIndex={0} onKeyPress={() => { setActiveGallery(prisonGallery); setIsGalleryOpen(true);} }>
              <img src={`${process.env.PUBLIC_URL}/prison-1.png`} alt="Prison Island" />
              <div className="tour-card-content">
                <h3>Prison Island (Changuu)</h3>
                <p>Meet giant Aldabra tortoises, explore historic ruins, and unwind on the turquoise shores of Prison Island.</p>
              </div>
            </div>
            <div className="tour-card" onClick={() => { setActiveGallery(sunsetGallery); setIsGalleryOpen(true);} } role="button" tabIndex={0} onKeyPress={() => { setActiveGallery(sunsetGallery); setIsGalleryOpen(true);} }>
              <img src={`${process.env.PUBLIC_URL}/sunset-1.png`} alt="Sunset Cruise" />
              <div className="tour-card-content">
                <h3>Sunset Dhow Cruise</h3>
                <p>Drift along Zanzibar’s coast as the sun sinks, with pastel skies, sea breeze, and a classic wooden dhow.</p>
              </div>
            </div>
            <div className="tour-card" onClick={() => { setActiveGallery(spiceGallery); setIsGalleryOpen(true);} } role="button" tabIndex={0} onKeyPress={() => { setActiveGallery(spiceGallery); setIsGalleryOpen(true);} }>
              <img src={`${process.env.PUBLIC_URL}/spice-5258020266449649078.png`} alt="Spice Farm" />
              <div className="tour-card-content">
                <h3>Spice Farm Experience</h3>
                <p>Walk the spice gardens, taste fresh cloves and cinnamon, and see how Zanzibar earned its Spice Island name.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {isGalleryOpen && (
        <div className="modal-overlay" onClick={() => setIsGalleryOpen(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setIsGalleryOpen(false)} aria-label="Close gallery">×</button>
            <h3>Tour Gallery</h3>
            <div className="gallery-grid modal-gallery">
              {activeGallery.map((src, idx) => (
                <div key={src} className="gallery-item">
                  <img src={src} alt={`Tour image ${idx + 1}`} />
                  <div className="gallery-overlay">
                    <h4>Tour Highlights</h4>
                    <p>Scenes from this experience</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Gallery Section */}
      <section id="gallery" className="gallery">
        <div className="container">
          <div className="section-title">
            <h2>Photo Gallery</h2>
            <p>Moments captured from our recent tours</p>
          </div>
          <div className="gallery-grid">
            {displayPictures.map((picture) => (
              <div key={picture.id} className="gallery-item">
                <img src={picture.imageUrl?.startsWith('/api') ? `http://localhost:8080/api/pictures/${picture.id}/image` : picture.imageUrl} alt={picture.title} />
                <div className="gallery-overlay">
                  <h4>{picture.title}</h4>
                  <p>{picture.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="testimonials">
        <div className="container">
          <div className="section-title">
            <h2>What Our Guests Say</h2>
            <p>Real experiences from real travelers</p>
          </div>
          <div className="testimonials-grid">
            {displayTestimonials.map((testimonial) => (
              <div key={testimonial.id} className="testimonial-card">
                <div className="stars">
                  {renderStars(testimonial.rating)}
                </div>
                <p>"{testimonial.message}"</p>
                <div className="testimonial-author">
                  <img src={testimonial.clientPhoto || 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100'} alt={testimonial.clientName} />
                  <div>
                    <h4>{testimonial.clientName}</h4>
                    <span>{testimonial.clientCountry}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="contact">
        <div className="container">
          <div className="section-title">
            <h2>Contact Us</h2>
            <p>Ready to start your Zanzibar adventure?</p>
          </div>
          <div className="contact-content">
            <div className="contact-info">
              <h3>Get in Touch</h3>
              <p>We'd love to hear from you! Reach out to us for bookings, inquiries, or just to say hello.</p>
              <div className="contact-details">
                <div className="contact-item">
                  <MapPin size={24} />
                  <span>Stone Town, Zanzibar, Tanzania</span>
                </div>
                <div className="contact-item">
                  <Phone size={24} />
                  <span>+255 702 963637</span>
                </div>
                <div className="contact-item">
                  <Mail size={24} />
                  <span>info@saitourzanzibar.com</span>
                </div>
              </div>
            </div>
            <div className="contact-form">
              <form onSubmit={handleContactSubmit}>
                <input 
                  type="text" 
                  name="name"
                  placeholder="Your Name" 
                  value={contactForm.name}
                  onChange={handleContactChange}
                  required
                />
                <input 
                  type="email" 
                  name="email"
                  placeholder="Your Email" 
                  value={contactForm.email}
                  onChange={handleContactChange}
                  required
                />
                <textarea 
                  name="message"
                  placeholder="Your Message"
                  value={contactForm.message}
                  onChange={handleContactChange}
                  required
                  rows="5"
                ></textarea>
                <button 
                  type="submit" 
                  className="btn btn-primary" 
                  style={{width: '100%'}}
                  disabled={contactStatus === 'Sending...'}
                >
                  {contactStatus === 'Sending...' ? 'Sending...' : 'Send Message'}
                </button>
                {contactStatus && (
                  <div style={{
                    marginTop: '10px',
                    padding: '10px',
                    backgroundColor: contactStatus.includes('success') ? '#d4edda' : '#f8d7da',
                    color: contactStatus.includes('success') ? '#155724' : '#721c24',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}>
                    {contactStatus}
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container footer-meta">
          <p className="footer-copy">
            &copy; {currentYear} <Logo className="footer-logo" height={20} /> All rights reserved.
          </p>
          <p>Made with love in Zanzibar</p>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
